import React, { useState, useMemo } from "react";
import { Avatar } from "../shared/Avatar";
import { CustomIcon } from "../shared/CustomIcon";
import { formatDate } from "../../lib/format";
import { apiCreateBlockout, apiUpdateBlockout } from "../../lib/calendarApi";

// Local copy of the role-label helper. Mirrors KingdomConnect.jsx::roleLabel —
// duplicated to avoid a back-edge import from /calendar → KingdomConnect.
function roleLabel(role) {
  if (role === "superadmin") return "Super Admin";
  if (role === "limited_admin") return "Limited Admin";
  if (role === "pastor") return "Pastor";
  return "Team Member";
}

// Pull the YYYY-MM-DD / HH:MM components out of a possibly-undefined ISO string.
function splitIso(iso, fallbackDate, fallbackTime) {
  if (!iso) return { date: fallbackDate, time: fallbackTime };
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return { date: fallbackDate, time: fallbackTime };
  const pad = (n) => String(n).padStart(2, "0");
  return {
    date: `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`,
    time: `${pad(dt.getHours())}:${pad(dt.getMinutes())}`,
  };
}

// ============================================================
// BlockoutFormModal — creates OR edits a blockout. When the `existing`
// prop is provided the form pre-fills with that record's fields and the
// submit handler PATCHes the existing record instead of creating a new
// one. Opened from the Calendar's Quick-Intake FAB and the inline edit
// pencil on the Blockouts panel.
// ============================================================
export default function BlockoutFormModal({
  currentUser, users, blockouts, setBlockouts, persist, addAudit, onClose, onSaved,
  existing, prefillDate,
}) {
  const todayStr = new Date().toISOString().slice(0, 10);
  // If a prefillDate was passed (Date or "YYYY-MM-DD"), honor it for a NEW blockout.
  const prefillStr = (() => {
    if (!prefillDate || existing) return todayStr;
    if (prefillDate instanceof Date) {
      const pad = (n) => String(n).padStart(2, "0");
      return `${prefillDate.getFullYear()}-${pad(prefillDate.getMonth() + 1)}-${pad(prefillDate.getDate())}`;
    }
    const s = String(prefillDate);
    return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : todayStr;
  })();
  const startSplit = splitIso(existing?.startDate, prefillStr, "09:00");
  const endSplit = splitIso(existing?.endDate, prefillStr, "17:00");
  const initialApplies = existing
    ? (existing.appliesTo === "all" || existing.appliesTo == null ? "all" : "specific")
    : "all";
  const initialUserIds = existing && Array.isArray(existing.appliesTo) ? existing.appliesTo : [];

  const [startDate, setStartDate] = useState(startSplit.date);
  const [startTime, setStartTime] = useState(startSplit.time);
  const [endDate, setEndDate] = useState(endSplit.date);
  const [endTime, setEndTime] = useState(endSplit.time);
  const [allDay, setAllDay] = useState(!!existing?.allDay);
  const [appliesMode, setAppliesMode] = useState(initialApplies);
  const [selectedUserIds, setSelectedUserIds] = useState(initialUserIds);
  const [userSearch, setUserSearch] = useState("");
  const [name, setName] = useState(existing?.name || "");
  const [description, setDescription] = useState(existing?.description || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    return (users || []).filter(u => !q || (u.name || "").toLowerCase().includes(q) || (u.username || "").toLowerCase().includes(q));
  }, [users, userSearch]);

  const toggleUser = (id) => {
    setSelectedUserIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const submit = async () => {
    setError("");
    if (!name.trim()) { setError("Please give the blockout a name."); return; }
    if (!startDate) { setError("Please pick a start date."); return; }
    if (!endDate) { setError("Please pick an end date."); return; }
    if (appliesMode === "specific" && selectedUserIds.length === 0) {
      setError("Please select at least one user, or choose All Users."); return;
    }
    const startIso = allDay ? `${startDate}T00:00:00` : `${startDate}T${startTime}:00`;
    const endIso   = allDay ? `${endDate}T23:59:59`   : `${endDate}T${endTime}:00`;
    if (new Date(endIso) < new Date(startIso)) { setError("End date/time must be after start."); return; }

    setBusy(true);
    const appliesTo = appliesMode === "all" ? "all" : selectedUserIds;
    const payload = {
      name: name.trim(),
      description: description.trim(),
      startDate: startIso,
      endDate: endIso,
      allDay,
      appliesTo,
      createdBy: existing?.createdBy || currentUser.id,
    };
    let saved;
    try {
      if (existing && existing.id) {
        saved = await apiUpdateBlockout(existing.id, payload);
      } else {
        // Use the dedicated backend endpoint — it both persists AND pushes
        // a Calendar-section bell notification to every affected user.
        saved = await apiCreateBlockout(payload);
      }
    } catch (e) {
      setBusy(false);
      alert(e.message || "Could not save blockout");
      return;
    }
    const next = existing && existing.id
      ? (blockouts || []).map(b => b.id === saved.id ? saved : b)
      : [...(blockouts || []), saved];
    setBlockouts(next);
    persist("kc_blockouts", next);
    addAudit && addAudit(currentUser.id, currentUser.name, existing ? "Updated blockout" : "Created blockout", `${saved.name} (${formatDate(startIso)})`);

    setBusy(false);
    onSaved && onSaved();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" data-testid="blockout-form-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <div className="modal-title">{existing ? "Edit Blockout" : "New Blockout"}</div>
          <button className="modal-close" data-testid="blockout-form-close" onClick={onClose} aria-label="Close"><CustomIcon name="close" size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label className="bo-form-label">Date Start</label>
              <div className="bo-form-row">
                <input className="form-input" type="date" data-testid="bo-start-date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                <input className="form-input" type="time" data-testid="bo-start-time" value={startTime} onChange={e => setStartTime(e.target.value)} disabled={allDay} />
              </div>
            </div>
            <div>
              <label className="bo-form-label">Date End</label>
              <div className="bo-form-row">
                <input className="form-input" type="date" data-testid="bo-end-date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                <input className="form-input" type="time" data-testid="bo-end-time" value={endTime} onChange={e => setEndTime(e.target.value)} disabled={allDay} />
              </div>
            </div>
            <label className="bo-allday" data-testid="bo-allday-label">
              <input type="checkbox" data-testid="bo-allday" checked={allDay} onChange={e => setAllDay(e.target.checked)} />
              <span>All day</span>
            </label>
            <div>
              <label className="bo-form-label">Applies To</label>
              <div className="bo-applies-options">
                <button type="button" data-testid="bo-applies-all" className={`bo-applies-pill ${appliesMode === "all" ? "active" : ""}`} onClick={() => setAppliesMode("all")}>All Users</button>
                <button type="button" data-testid="bo-applies-specific" className={`bo-applies-pill ${appliesMode === "specific" ? "active" : ""}`} onClick={() => setAppliesMode("specific")}>Specific Users</button>
              </div>
              {appliesMode === "specific" && (
                <>
                  <input className="form-input" type="search" placeholder="Search users…" data-testid="bo-user-search" value={userSearch} onChange={e => setUserSearch(e.target.value)} style={{ marginBottom: 8 }} />
                  <div className="bo-user-picker" data-testid="bo-user-picker">
                    {filteredUsers.length === 0 && (
                      <div style={{ padding: 12, fontSize: 12, color: "var(--kc-meta)", textAlign: "center" }}>No users match.</div>
                    )}
                    {filteredUsers.map(u => {
                      const sel = selectedUserIds.includes(u.id);
                      return (
                        <div key={u.id} className={`bo-user-row ${sel ? "selected" : ""}`} data-testid={`bo-user-${u.id}`} onClick={() => toggleUser(u.id)}>
                          <span className="bo-user-check">{sel ? <CustomIcon name="check" size={12} /> : ""}</span>
                          <Avatar name={u.name} photo={u.photo} memberId={u.memberId} style={{ width: 24, height: 24, fontSize: 10 }} />
                          <span style={{ flex: 1 }}>{u.name}</span>
                          <span style={{ fontSize: 10, color: "var(--kc-meta)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>{roleLabel(u.role)}</span>
                        </div>
                      );
                    })}
                  </div>
                  {selectedUserIds.length > 0 && (
                    <div style={{ marginTop: 6, fontSize: 11, color: "var(--kc-sub)", fontFamily: "'Montserrat',sans-serif" }}>
                      {selectedUserIds.length} user{selectedUserIds.length === 1 ? "" : "s"} selected
                    </div>
                  )}
                </>
              )}
            </div>
            <div>
              <label className="bo-form-label" htmlFor="bo-name">Name</label>
              <input id="bo-name" data-testid="bo-name" className="form-input" type="text" placeholder="e.g. Annual leave" value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div>
              <label className="bo-form-label" htmlFor="bo-desc">Description</label>
              <textarea id="bo-desc" data-testid="bo-description" className="form-input" rows={3} placeholder="Optional details…" value={description} onChange={e => setDescription(e.target.value)} />
            </div>
            {error && (
              <div data-testid="bo-error" style={{ background: "rgba(184,50,50,0.12)", border: "1px solid rgba(184,50,50,0.35)", color: "#ff8a8a", padding: "10px 12px", borderRadius: 8, fontFamily: "'Montserrat',sans-serif", fontSize: 12 }}>
                {error}
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" data-testid="bo-cancel" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" data-testid="bo-create" onClick={submit} disabled={busy}>{busy ? "Saving…" : existing ? "Save changes" : "Create"}</button>
        </div>
      </div>
    </div>
  );
}
